import { ROUTES } from 'constants/routes';

export type SchoolThoughtStatus = 'available' | 'coming_soon';

export type SchoolOfThought = {
  id: string;
  name: string;
  description: string;
  status: SchoolThoughtStatus;
  href?: string;
  icon: string;
  slug?: string;
};

export type SchoolOfThoughtCategory = {
  id: string;
  title: string;
  shortTitle?: string;
  schools: SchoolOfThought[];
};

export const AVAILABLE_SCHOOL_IDS = ['parashari', 'jaimini', 'bnn', 'kp', 'lal-kitab', 'numerology', 'surya-siddhanta', 'panchang', 'vastu', 'swara-vigyan', 'yantra-vidya', 'tantra'] as const;

export const schoolsOfThoughtCategories: SchoolOfThoughtCategory[] = [
  {
    id: 'astrological-systems',
    title: 'Astrological Systems (Based on Planetary Movements)',
    shortTitle: 'Astrology',
    schools: [
      {
        id: 'parashari',
        name: 'Parashari Jyotish',
        description:
          'The classical mainstream of Vedic astrology - grahas, bhavas, yogas, divisional charts, and Vimshottari Dasha rooted in Brihat Parashara Hora Shastra.',
        status: 'available',
        href: ROUTES.PARASHARI_LEARN,
        icon: 'orbit',
        slug: 'parashari',
      },
      {
        id: 'jaimini',
        name: 'Jaimini Astrology',
        description:
          'A complementary sign-based lens - Chara Karakas, Rashi Drishti, Arudha Padas, and Chara Dasha on the same birth chart.',
        status: 'available',
        href: ROUTES.JAIMINI_LEARN,
        icon: 'network',
        slug: 'jaimini',
      },
      {
        id: 'tajika',
        name: 'Tajika System',
        description:
          'A branch of Vedic astrology focusing on annual solar returns (Varshaphala) to predict events for a specific year.',
        status: 'coming_soon',
        icon: 'sun',
      },
      {
        id: 'western',
        name: 'Western Astrology',
        description:
          'Uses tropical zodiac coordinates, planetary transits, secondary progressions, and solar arc directions.',
        status: 'coming_soon',
        icon: 'globe',
      },
      {
        id: 'kp',
        name: 'KP System (Krishnamurti Paddhati)',
        description:
          'A highly precise Indian system based on stellar constellations (Nakshatras) and sub-lords.',
        status: 'available',
        href: ROUTES.KP_LEARN,
        icon: 'grid',
        slug: 'kp',
      },
      {
        id: 'bnn',
        name: 'Bhrigu Nandi Nadi',
        description:
          'Ancient Nadi astrology without Lagna or Dashas - read life through fixed karakatwas, planetary sambandha, and Jupiter/Saturn transits.',
        status: 'available',
        href: ROUTES.BNN_LEARN,
        icon: 'link',
        slug: 'bnn',
      },
      {
        id: 'lal-kitab',
        name: 'Lal Kitab',
        description:
          'A unique blend of astrology and palmistry with a heavy emphasis on quick remedial measures.',
        status: 'available',
        href: ROUTES.LAL_KITAB_LEARN,
        icon: 'book',
        slug: 'lal-kitab',
      },
      {
        id: 'surya-siddhanta',
        name: 'Surya Siddhanta',
        description:
          'Classical Ganita Jyotish - Mahayuga time cycles, planetary periods, Lahiri ayanamsha, and the math behind Panchang.',
        status: 'available',
        href: ROUTES.SURYA_SIDDHANTA_LEARN,
        icon: 'sun',
        slug: 'surya-siddhanta',
      },
      {
        id: 'panchang',
        name: 'Panchang',
        description:
          'The Hindu calendar of auspicious timing - tithi, vara, nakshatra, yoga, karana, balas, doshas, and muhurta for daily life.',
        status: 'available',
        href: ROUTES.PANCHANG_LEARN,
        icon: 'calendar',
        slug: 'panchang',
      },
      {
        id: 'vastu',
        name: 'Vastu Shastra',
        description:
          'Align built space with directional energies - Vastu Purusha mandala, residential and commercial zoning, padas, and floor-plan audit.',
        status: 'available',
        href: ROUTES.VASTU_LEARN,
        icon: 'compass',
        slug: 'vastu',
      },
      {
        id: 'swara-vigyan',
        name: 'Swara Vigyan',
        description:
          'Learn which nostril is active, moon-day rules at sunrise, and when to eat, travel, study, or rest - in plain language.',
        status: 'available',
        href: ROUTES.SWARA_VIGYAN_LEARN,
        icon: 'wind',
        slug: 'swara-vigyan',
      },
      {
        id: 'yantra-vidya',
        name: 'Yantra Vidya',
        description:
          'Sacred geometry drawn step by step - bindu, triangles, lotus, gates, and how to read yantra layers in plain language.',
        status: 'available',
        href: ROUTES.YANTRA_VIDYA_LEARN,
        icon: 'hexagon',
        slug: 'yantra-vidya',
      },
      {
        id: 'tantra',
        name: 'Tantra',
        description:
          'Philosophy, scriptures, and practice vocabulary - Shiva–Shakti, mantra, nyāsa, and ethical study with guru guidance.',
        status: 'available',
        href: ROUTES.TANTRA_LEARN,
        icon: 'flame',
        slug: 'tantra',
      },
      {
        id: 'chinese',
        name: 'Chinese Astrology (BaZi / Zi Wei Dou Shu)',
        description:
          'Predicts destiny based on the Four Pillars of Destiny (birth year, month, day, hour) or the Purple Star system.',
        status: 'coming_soon',
        icon: 'yin-yang',
      },
    ],
  },
  {
    id: 'cartomancy',
    title: 'Cartomancy & Symbol-Based Divination',
    shortTitle: 'Cartomancy',
    schools: [
      {
        id: 'tarot',
        name: 'Tarot Cards',
        description:
          'A 78-card system utilizing archetypal imagery, split into the Major and Minor Arcana, to read energy and future trajectories.',
        status: 'coming_soon',
        icon: 'sparkles',
      },
      {
        id: 'lenormand',
        name: 'Lenormand Cards',
        description:
          'A 36-card deck focused on direct, practical, and everyday situational predictions.',
        status: 'coming_soon',
        icon: 'layers',
      },
      {
        id: 'oracle',
        name: 'Angel / Oracle Cards',
        description:
          'Intuitive decks with fluid meanings, used mostly for guidance, spiritual alignment, and general forecasting.',
        status: 'coming_soon',
        icon: 'feather',
      },
      {
        id: 'runes',
        name: 'Runes',
        description:
          'An ancient Germanic alphabet (often Elder Futhark) carved onto stones or wood, cast to read situational outcomes.',
        status: 'coming_soon',
        icon: 'dice',
      },
      {
        id: 'i-ching',
        name: 'I Ching (Book of Changes)',
        description:
          'An ancient Chinese divination text used by casting coins or yarrow stalks to generate hexagrams that describe future changes.',
        status: 'coming_soon',
        icon: 'hexagon',
      },
    ],
  },
  {
    id: 'body-feature',
    title: 'Body Feature Systems (Somatic Divination)',
    shortTitle: 'Body Reading',
    schools: [
      {
        id: 'palmistry',
        name: 'Palmistry (Chirognomy / Chiromancy)',
        description:
          'Evaluating the lines, mounts, shapes, and textures of the hands to read life span, career, and relationships.',
        status: 'coming_soon',
        icon: 'hand',
        slug: 'palmistry',
      },
      {
        id: 'physiognomy',
        name: 'Physiognomy (Face Reading)',
        description:
          'An ancient practice (highly developed in Chinese Mian Xiang) that maps life phases and luck onto facial structures.',
        status: 'coming_soon',
        icon: 'eye',
      },
      {
        id: 'samudrika',
        name: 'Samudrika Shastra',
        description:
          'A vast Vedic study of whole-body features, moles, signs, and physical body structures to determine destiny.',
        status: 'coming_soon',
        icon: 'scan',
      },
    ],
  },
  {
    id: 'mathematical',
    title: 'Mathematical & Numeric Systems',
    shortTitle: 'Numerology',
    schools: [
      {
        id: 'numerology',
        name: 'Numerology (Chaldean)',
        description:
          'Chaldean sound-based letter chart, compound numbers 10–52, Mulank, Bhagyank, name analysis, and prescriptive professional practice.',
        status: 'available',
        href: ROUTES.NUMEROLOGY_LEARN,
        icon: 'hash',
        slug: 'numerology',
      },
      {
        id: 'lo-shu',
        name: 'Lo Shu Grid',
        description:
          'A Chinese numerology method based on a 3 × 3 magic square to analyze destiny and elemental balances.',
        status: 'coming_soon',
        icon: 'layout',
      },
    ],
  },
  {
    id: 'intuitive-omens',
    title: 'Intuitive, Omens & Question-Based Systems',
    shortTitle: 'Omens & Prashna',
    schools: [
      {
        id: 'horary',
        name: 'Horary Astrology / Prashna Shastra',
        description:
          'Casting an astrological chart for the exact second a client asks a specific question to find the immediate answer.',
        status: 'coming_soon',
        icon: 'clock',
      },
      {
        id: 'ramala',
        name: 'Prashna Kundali / Ramala',
        description:
          'Divination systems using dice, dots, or random numbers generated by a client to answer immediate questions.',
        status: 'coming_soon',
        icon: 'dice-5',
      },
      {
        id: 'nimitta',
        name: 'Nimitta Shastra',
        description:
          'The study of environmental omens, animal behaviors, and sudden natural occurrences to predict immediate future outcomes.',
        status: 'coming_soon',
        icon: 'bird',
      },
    ],
  },
];

export function getAvailableSchools(): SchoolOfThought[] {
  return schoolsOfThoughtCategories
    .flatMap((category) => category.schools)
    .filter((school) => school.status === 'available');
}

export function getComingSoonSchools(): SchoolOfThought[] {
  return schoolsOfThoughtCategories
    .flatMap((category) => category.schools)
    .filter((school) => school.status !== 'available');
}

export function getAllSchools(): SchoolOfThought[] {
  return schoolsOfThoughtCategories.flatMap((category) => category.schools);
}
